import { Raffle } from '@/app/entities/Raffle'
import { useRaffleMutations } from '@/app/hooks/useRaffleMutations'
import { ImageUpload } from '@/view/components/ImageUpload'
import { Button } from '@/view/components/ui/button'
import { DatePicker } from '@/view/components/ui/date-picker'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import { Input } from '@/view/components/ui/input'
import { Textarea } from '@/view/components/ui/textarea'
import { PlusCircle, X } from 'lucide-react'
import { CurrencyInput } from 'react-currency-mask'
import { useFieldArray } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Fragment } from 'react/jsx-runtime'
import { ThumnailDropzone } from '../../Conteudos/ClassDetails/components/ThumbnailDropzone'
import { useRaffleForm } from '../hooks/useRaffleForm'
import { getRaffleFormDefaultValues } from '../utils'
import { Checkbox } from '@/view/components/ui/checkbox'
import { useState } from 'react'
import { useAuth } from '@/app/hooks/useAuth'

interface RaffleFormProps {
  raffle: Raffle | undefined
}

export const RaffleForm = ({ raffle }: RaffleFormProps) => {
  const isEdit = !!raffle
  const navigate = useNavigate()
  const { isAdmin } = useAuth()

  const [showRaffleCodeInput, setShowRaffleCodeInput] = useState(false)

  const form = useRaffleForm({
    getDefaultValues: raffle
      ? () => getRaffleFormDefaultValues(raffle)
      : undefined,
  })

  const isLoadingForm = form.formState.isLoading

  const { createRaffle, updateRaffle } = useRaffleMutations()

  const handleSubmit = form.handleSubmit(async (data) => {
    const payload = {
      ...data,
      couponValue: Math.trunc(data.couponValue * 100),
    }
    try {
      if (isEdit) {
        await updateRaffle.mutateAsync({
          raffleId: raffle?._id,
          payload,
        })
      } else {
        await createRaffle.mutateAsync(payload)
      }
      navigate('/sorteios/gerenciar')
    } catch (error) {
      console.error(error)
    }
  }, console.error)

  const description = form.watch('description')

  const { fields, append } = useFieldArray({
    control: form.control,
    name: 'prizes',
  })

  const handleAddPrize = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    append({ name: '' })
  }

  const handleCancel = () => {
    navigate(-1)
  }

  return (
    <Form {...form}>
      <form
        className="mt-6 grid w-full max-w-6xl grid-cols-1 gap-6 sm:mt-8 lg:grid-cols-3 lg:gap-8"
        onSubmit={handleSubmit}
      >
        <div className="space-y-4 sm:space-y-5 lg:col-span-2">
          {isAdmin && false && (
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={showRaffleCodeInput}
                  onCheckedChange={(checked) =>
                    setShowRaffleCodeInput(!!checked)
                  }
                  id="raffleCode"
                />
                <span className="text-sm">
                  Informar identificador do sorteio
                </span>
              </label>

              {showRaffleCodeInput && (
                <FormField
                  control={form.control}
                  name="identificadorSorteioStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="Código do sorteio"
                          disabled={isLoadingForm}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          )}

          <FormField
            defaultValue=""
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nome do sorteio</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Sorteio Exemplo"
                    disabled={isLoadingForm}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="couponValue"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Valor em vendas</FormLabel>
                <FormControl>
                  <CurrencyInput
                    value={field.value}
                    onChangeValue={(_, originalValue) => {
                      field.onChange(originalValue)
                    }}
                    InputElement={
                      <Input
                        placeholder="R$ 0,00"
                        ref={field.ref}
                        disabled={isLoadingForm}
                      />
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-4 sm:flex-row sm:gap-4">
            <FormField
              defaultValue={new Date()}
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Data de início</FormLabel>
                  <FormControl>
                    <DatePicker
                      disabled={isLoadingForm}
                      onChange={field.onChange}
                      selected={field.value ?? new Date()}
                      label="Data de início"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              defaultValue={new Date()}
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Data de encerramento</FormLabel>
                  <FormControl>
                    <DatePicker
                      disabled={isLoadingForm}
                      onChange={field.onChange}
                      selected={field.value ?? new Date()}
                      label="Data de encerramento"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              defaultValue={new Date()}
              control={form.control}
              name="resultDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Data do resultado</FormLabel>
                  <FormControl>
                    <DatePicker
                      disabled={isLoadingForm}
                      onChange={field.onChange}
                      selected={field.value ?? new Date()}
                      label="Data de resultado"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            defaultValue=""
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Explique sua campanha"
                    disabled={isLoadingForm}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <div className="flex justify-end">
                  <span className="text-sm text-muted-foreground">
                    {description?.length}/500
                  </span>
                </div>
              </FormItem>
            )}
          />

          <div className="w-full space-y-3">
            <FormField
              control={form.control}
              name="prizes"
              render={() => (
                <Fragment>
                  <FormLabel>Prêmios</FormLabel>
                  {fields.map((field, index) => (
                    <FormField
                      key={field.id}
                      control={form.control}
                      name={`prizes.${index}.name`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              placeholder="Prêmio Exemplo"
                              disabled={isLoadingForm}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </Fragment>
              )}
            />
            <Button
              onClick={handleAddPrize}
              variant="outline"
              className="w-full rounded-full font-semibold text-primary"
              size="lg"
              disabled={isLoadingForm}
            >
              <PlusCircle size={20} className="mr-2" />
              Novo prêmio
            </Button>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:gap-4">
            <Button
              type="button"
              variant="ghost"
              className="w-full rounded-full sm:w-auto"
              disabled={isLoadingForm}
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              disabled={
                createRaffle.isPending ||
                updateRaffle.isPending ||
                isLoadingForm
              }
              type="submit"
              className="w-full rounded-full bg-primary text-white sm:w-auto"
            >
              {raffle ? 'Atualizar sorteio' : 'Criar sorteio'}
            </Button>
          </div>
        </div>
        <div className="order-first w-full space-y-4 lg:order-none lg:col-span-1">
          <FormField
            control={form.control}
            name="banner"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Capa do sorteio</FormLabel>
                <ThumnailDropzone
                  {...field}
                  className="h-[280px] w-full sm:h-[350px] lg:h-[451px]"
                  onChange={field.onChange}
                  value={field.value ?? undefined}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="gallery"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Imagens do sorteio (Opcional)</FormLabel>
                <ImageUpload
                  onChange={field.onChange}
                  value={field.value ?? null}
                  render={({ value }) => {
                    const handleRemove =
                      (image: File | string) =>
                      (e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault()
                        e.stopPropagation()
                        field.onChange(field.value?.filter((i) => i !== image))
                      }

                    if (!value?.length) return <ImageUpload.Placeholder />
                    return (
                      <div className="grid grid-cols-3 gap-4">
                        {value.map((image) => {
                          const isFile = image instanceof File
                          const key = isFile ? image.name : image
                          const src = isFile
                            ? URL.createObjectURL(image)
                            : image
                          return (
                            <div className="group relative" key={key}>
                              <img
                                src={src}
                                alt=""
                                className="h-full w-full rounded-md object-cover"
                              />
                              <Button
                                size="icon"
                                className="absolute right-1 top-1 h-5 w-5 rounded-full text-white opacity-0 transition-opacity group-hover:opacity-100"
                                onClick={handleRemove(image)}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
